import { createContext, useContext } from 'react';
import type { ConnectApi } from './useConnect';
import type { SelectionApi } from './useSelection';
import type { ViewportApi } from './useViewport';

/** The gesture handlers and callbacks every canvas child needs, stable for the canvas's life. */
export interface CanvasRuntime {
  viewport: ViewportApi;
  selection: SelectionApi;
  connect: ConnectApi;
  /** Hand a node element to the shared ResizeObserver; null stops watching it. */
  observe: (id: string, el: HTMLElement | null) => void;
  savePositions: (ids: string[]) => void;
  /** A drag ended: save every moved node, and file each dragged root into the group it landed in. */
  finishDrag: (ids: string[]) => void;
  /** Save a group's size from the store after a corner resize. */
  saveSize: (id: string) => void;
  /** Everything a group holds, at any depth; empty for a topic. */
  descendantsOf: (id: string) => string[];
  parentOf: (id: string) => string | null;
  /** The innermost group under this node's center right now, never itself or one it holds. */
  groupUnder: (id: string) => string | null;
  /** May this person move this node: a manager, or its authority, on a writable plan. */
  canMove: (id: string) => boolean;
  /** Select one node and open its side panel; `focus` moves keyboard focus into it (Enter). */
  openPanel: (id: string, focus: boolean) => void;
}

export const CanvasRuntimeContext = createContext<CanvasRuntime | null>(null);

export const useRuntime = (): CanvasRuntime => {
  const rt = useContext(CanvasRuntimeContext);
  if (!rt) throw new Error('Canvas runtime used outside <Canvas>.');
  return rt;
};
